// v49.24 — Confirm dialog.
// Promise-based reusable confirmation modal. Replaces window.confirm()
// across destructive actions (delete, cancel, complete-and-lock,
// withdraw, etc) so the UX matches the v48 lcConfirm() pattern.
//
// Usage:
//   const confirm = useConfirm();
//   const ok = await confirm({
//     title: "Delete project?",
//     message: "This can't be undone.",
//     confirmLabel: "Delete",
//     variant: "danger",
//   });
//   if (!ok) return;

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button";

export type ConfirmVariant = "standard" | "danger";

export interface ConfirmOptions {
  title:         string;
  message?:      string;
  confirmLabel?: string;
  cancelLabel?:  string;
  variant?:      ConfirmVariant;
}

type Resolver = (ok: boolean) => void;

interface ConfirmCtx {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const Ctx = createContext<ConfirmCtx | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      resolverRef.current = resolve;
      setActive(opts);
    });
  }, []);

  const close = (ok: boolean) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setActive(null);
    r?.(ok);
  };

  const variant = active?.variant ?? "standard";

  return (
    <Ctx.Provider value={{ confirm }}>
      {children}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={() => close(false)}
        >
          <div
            className="bg-white rounded-lc-lg shadow-2xl w-full max-w-md flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-5 flex gap-4">
              <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                variant === "danger" ? "bg-lc-red-bg text-lc-red-fg" : "bg-lc-blue-light text-lc-blue-dark"
              }`}>
                {variant === "danger" ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-extrabold text-lc-ink tracking-tight">{active.title}</h2>
                {active.message && (
                  <p className="text-sm text-lc-mute mt-1.5 leading-snug">{active.message}</p>
                )}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-lc-line bg-lc-bg-2/40 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => close(false)}>
                {active.cancelLabel || "Cancel"}
              </Button>
              <Button
                type="button"
                variant={variant === "danger" ? "danger" : "primary"}
                onClick={() => close(true)}
              >
                {active.confirmLabel || "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}
